import { isCroreMetric } from '@/features/screener/lib/filters'
import { formatMoney, formatQuantity } from '@/lib/format'
import { div, toChartNumber, type Money } from '@/lib/money'
import type { Metric } from '@/schemas/screener'

/**
 * How a metric's figure reads in a cell, by its unit, following the mock wherever it draws the
 * column: a price to the paisa, a company total in whole crore ("Rs 3,420 Cr"), a ratio to two
 * places, relative volume as a multiple ("2.10×"), a share count in lakh from one lakh up
 * ("2.51 L"). The moves (`MOVE_METRICS`) are drawn by `Change` instead, signed and coloured.
 */

/** Percent metrics that are a price moving, so they read signed and in the market colours. */
export const MOVE_METRICS: ReadonlySet<string> = new Set([
  'change_percent',
  'performance_1w',
  'market_cap_change_1y',
  'change_since_ipo',
])

export const isMoveMetric = (metric: Metric): boolean =>
  metric.unit === 'percent' && MOVE_METRICS.has(metric.key)

const LAKH = 100_000

const crore = (value: Money): string =>
  `Rs ${formatMoney(div(value, 10_000_000), { whole: true })} Cr`

const shares = (value: Money): string => {
  const count = toChartNumber(value)
  return Math.abs(count) >= LAKH
    ? `${(count / LAKH).toFixed(2)} L`
    : formatQuantity(Math.round(count))
}

/** A non-null figure as its cell prints it. A move's text is `Change`'s, not this. */
export const formatMetric = (metric: Metric, value: Money): string => {
  if (isCroreMetric(metric.key)) return crore(value)
  switch (metric.unit) {
    case 'npr':
    case 'number':
      return formatMoney(value)
    case 'percent':
      return `${formatMoney(value)}%`
    case 'times':
      return metric.key === 'rvol' ? `${formatMoney(value)}×` : formatMoney(value)
    case 'shares':
      return shares(value)
  }
}
